import { useNavigate, useSearchParams } from 'react-router-dom';
import { useCallback, useEffect, useState, type JSX } from 'react';
import orderPlacedImage from '@/assets/images/orderPlaced.png';
import ForwardArrow from '@/assets/svg/static/arrow.svg';
import ExchangeIcon from '@/assets/svg/static/exchangeBox.svg';
import FeesIcon from '@/assets/svg/static/fees.svg';
import slaydTextIcon from '@/assets/svg/static/slaydText.svg';
import { useViewabilityTracker } from '@/hooks/useViewabilityTracker';
import {
  EVENTS,
  PROPERTIES,
  trackEvent,
  TRACKING_SOURCES,
} from '@/services/posthog';
import {
  fetchGuestOrderDetail,
  fetchProductDataById,
} from '@/services/slaydAPI';
import useDownloadAppSheetStore from '@/stores/DownloadAppSheetStore';
import useLayoutStore from '@/stores/useLayoutStore';
import { OrderStatus, type GuestOrder } from '@/types/Order.types';
import { capitalizeFirstLetter, updatedImageUrl } from '@/utils/formatting';
import {
  // CONTINUE_SHOPPING_TEXT,
  DELIVERY_ESTIMATE_TEXT,
  DELIVERY_FEE,
  DELIVERY_PARTNER_TEXT,
  FALLBACK_BUTTON_TEXT,
  FALLBACK_SUBTITLE,
  FALLBACK_TITLE,
  LABEL_CANCEL_RETURN,
  LABEL_DELIVERY_FEES,
  LABEL_DELIVERY_FEES_STATIC,
  LABEL_DISCOUNT,
  LABEL_FINAL_PRICE,
  LABEL_PRODUCT_PRICE,
  LABEL_TRACK_ORDER,
  ORDER_PLACED_TITLE,
  RETURN_POLICY_TEXT,
  SECTION_DELIVERY_ADDRESS,
  SECTION_MODE_OF_PAYMENT,
  SECTION_PAYMENT_DETAILS,
} from './OrderPlacedPage.constants';
import styles from './OrderPlacedPage.module.css';

const getPaymentModeLabel = (mode?: string | null): string => {
  if (!mode) return '—';
  if (mode.toLowerCase() === 'cod') return 'Cash on Delivery';
  if (mode.toLowerCase() === 'prepaid') return 'Online (Prepaid)';
  return mode;
};

const safeParseFloat = (val: string | null | undefined): number => {
  if (!val || val === 'null' || val === 'undefined') return 0;
  const parsed = parseFloat(val);
  return isNaN(parsed) ? 0 : parsed;
};

interface OrderItemWithTrackingProps {
  item: {
    productId: number;
    productName: string;
    brandName?: string;
    dealPriceAtPurchase: string | null;
    mrpAtPurchase: string;
    size?: string | null;
    quantity: number;
    imageUrl?: string;
  };
  guestOrderId: string;
}

const OrderItemWithTracking = ({
  item,
  guestOrderId,
}: OrderItemWithTrackingProps): JSX.Element => {
  const viewRef = useViewabilityTracker<HTMLDivElement>({
    onViewable: () => {
      trackEvent(EVENTS.ORDER_PLACED_ITEM_VIEWED, {
        [PROPERTIES.GUEST_ORDER_ID]: guestOrderId,
        [PROPERTIES.PRODUCT_ID]: item.productId,
        [PROPERTIES.PRODUCT_NAME]: item.productName,
        [PROPERTIES.BRAND]: item.brandName || '',
        [PROPERTIES.PRICE]: parseFloat(
          item.dealPriceAtPurchase || item.mrpAtPurchase || '0'
        ),
        [PROPERTIES.SIZE]: item.size || '',
        [PROPERTIES.QUANTITY]: item.quantity,
        [PROPERTIES.SOURCE]: TRACKING_SOURCES.PDP_V2,
      });
    },
    threshold: 0.5,
    once: true,
  });

  return (
    <div ref={viewRef} className={styles.productRow}>
      <div className={styles.productImageWrapper}>
        <img
          src={updatedImageUrl(item.imageUrl || '')}
          alt={item.productName}
          className={styles.productImage}
        />
      </div>
      <div className={styles.productRowRight}>
        <div className={styles.productInfo}>
          <h3 className={styles.productBrand}>{item.brandName || ''}</h3>
          <p className={styles.productName}>{item.productName}</p>
          <p className={styles.productMeta}>
            {'Size '}
            <span className={styles.productMetaBold}>{item.size ?? '—'}</span>
            {'   Qty '}
            <span className={styles.productMetaBold}>{item.quantity}</span>
          </p>
          <div className={styles.productPriceRow}>
            <span className={styles.productPriceSale}>
              {`₹${parseFloat(
                item.dealPriceAtPurchase || item.mrpAtPurchase || '0'
              ).toFixed(2)}`}
            </span>
            <span className={styles.productPriceOriginal}>
              {`₹${parseFloat(item.mrpAtPurchase).toFixed(2)}`}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default function OrderPlacedPage(): JSX.Element {
  const [searchParams] = useSearchParams();
  const guestOrderId = searchParams.get('guestOrderId');
  const rawCuratorName = searchParams.get('curatorName');
  const influencerSlug = rawCuratorName
    ? capitalizeFirstLetter(rawCuratorName)
    : null;
  const navigate = useNavigate();

  const [order, setOrder] = useState<GuestOrder | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  const setNavbarVisibility = useLayoutStore(
    (state) => state.setNavbarVisibility
  );
  const { openSheet: openDownloadSheet } = useDownloadAppSheetStore();

  useEffect(() => {
    setNavbarVisibility(false);
    return () => {
      setNavbarVisibility(true);
    };
  }, [setNavbarVisibility]);

  const loadOrder = useCallback(async (): Promise<void> => {
    if (!guestOrderId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const guestOrder = await fetchGuestOrderDetail(guestOrderId);

      await Promise.all(
        guestOrder.items.map(async (item) => {
          try {
            const productData = await fetchProductDataById(
              item.productId.toString()
            );
            item.imageUrl = productData?.imageUrl || '';
            item.brandName =
              productData?.brand || productData?.brandDetails?.brandName || '';
          } catch (err) {
            console.error(
              `Failed to fetch product metadata for ID ${item.productId}`,
              err
            );
          }
        })
      );

      setOrder(guestOrder);
    } catch (err) {
      console.error('[OrderPlacedPage] loadOrder failed:', err);
      trackEvent(EVENTS.ORDER_PLACED_LOAD_FAILED, {
        [PROPERTIES.GUEST_ORDER_ID]: guestOrderId || '',
        [PROPERTIES.FAILURE_REASON]:
          err instanceof Error ? err.message : String(err),
        [PROPERTIES.SOURCE]: TRACKING_SOURCES.PDP_V2,
      });
    } finally {
      setLoading(false);
    }
  }, [guestOrderId]);

  useEffect(() => {
    if (!guestOrderId) {
      navigate('/');
      return;
    }
    loadOrder();
  }, [guestOrderId, navigate, loadOrder]);

  useEffect(() => {
    if (order) {
      const productIds = (order.items || [])
        .map((i) => i.productId.toString())
        .join(',');
      const productPrice = (order.items || []).reduce((sum, item) => {
        const price = safeParseFloat(
          item.dealPriceAtPurchase || item.mrpAtPurchase
        );
        return sum + price * item.quantity;
      }, 0);
      const finalPrice = order.razorpayAmount
        ? safeParseFloat(order.razorpayAmount)
        : productPrice;

      trackEvent(EVENTS.ORDER_PLACED_VIEW, {
        [PROPERTIES.GUEST_ORDER_ID]: order.guestOrderId,
        [PROPERTIES.STATUS]: order.status,
        [PROPERTIES.AMOUNT]: finalPrice,
        [PROPERTIES.ITEM_COUNT]: order.items?.length || 0,
        [PROPERTIES.PAYMENT_MODE]: order.paymentMode || '',
        [PROPERTIES.PRODUCT_IDS]: productIds,
        [PROPERTIES.SOURCE]: TRACKING_SOURCES.PDP_V2,
      });
    }
  }, [order]);

  useEffect(() => {
    if (!loading && !order && guestOrderId) {
      trackEvent(EVENTS.ORDER_PLACED_FALLBACK_VIEW, {
        [PROPERTIES.GUEST_ORDER_ID]: guestOrderId,
        [PROPERTIES.SOURCE]: TRACKING_SOURCES.PDP_V2,
      });
    }
  }, [loading, order, guestOrderId]);

  // const handleContinueShopping = (): void => {
  //   trackEvent(EVENTS.ORDER_PLACED_CONTINUE_CLICK, {
  //     [PROPERTIES.GUEST_ORDER_ID]: guestOrderId || '',
  //     [PROPERTIES.SOURCE]: TRACKING_SOURCES.PDP_V2,
  //   });
  //   navigate('/');
  // };

  const handleActionClick = (actionName: string): void => {
    trackEvent(EVENTS.ORDER_PLACED_ACTION_CLICK, {
      [PROPERTIES.ACTION]: actionName,
      [PROPERTIES.GUEST_ORDER_ID]: guestOrderId || '',
      [PROPERTIES.SOURCE]: TRACKING_SOURCES.PDP_V2,
    });

    const whatsappOrderId = order?.orderId
      ? String(order.orderId)
      : String(order?.guestOrderId || guestOrderId || '');

    const message =
      actionName === 'track_order'
        ? `Hi, I'd like to track my order #${whatsappOrderId}.`
        : `Hi, I'd like to request a cancellation/return/refund/exchange for my order #${whatsappOrderId}.`;

    const whatsappUrl = `https://wa.me/916395347884?text=${encodeURIComponent(
      message
    )}`;
    window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
  };

  const handleFallbackClick = (): void => {
    trackEvent(EVENTS.ORDER_PLACED_FALLBACK_CLICK, {
      [PROPERTIES.ACTION]: 'fallback_my_orders',
      [PROPERTIES.SOURCE]: TRACKING_SOURCES.PDP_V2,
    });
    openDownloadSheet('personalize');
  };

  const handleHeaderClick = (): void => {
    const curationId = searchParams.get('curation_id');
    const key = searchParams.get('key');
    const rid = searchParams.get('rid');
    const variant = searchParams.get('variant');
    const fbclid = searchParams.get('fbclid');

    if (influencerSlug && curationId) {
      const params = new URLSearchParams();
      params.set('curatorName', influencerSlug);
      params.set('curation_id', curationId);
      if (key) params.set('key', key);
      if (rid) params.set('rid', rid);
      if (variant) params.set('variant', variant);
      if (fbclid) params.set('fbclid', fbclid);

      trackEvent(EVENTS.ORDER_PLACED_HEADER_STORE_CLICK, {
        [PROPERTIES.GUEST_ORDER_ID]: guestOrderId || '',
        [PROPERTIES.CURATOR_NAME]: influencerSlug,
        [PROPERTIES.CURATION_ID]: curationId,
        [PROPERTIES.SOURCE]: 'order_placed',
        validation_passed: true,
      });

      navigate(`/curator-store?${params.toString()}`);
    } else {
      trackEvent(EVENTS.ORDER_PLACED_HEADER_STORE_CLICK_FAILED, {
        [PROPERTIES.GUEST_ORDER_ID]: guestOrderId || '',
        [PROPERTIES.CURATOR_NAME]: influencerSlug || '',
        [PROPERTIES.CURATION_ID]: curationId || '',
        [PROPERTIES.FAILURE_REASON]: 'missing_curator_or_curation_id',
        validation_passed: false,
      });
      navigate('/');
    }
  };

  if (loading) {
    return (
      <div className={styles.parentContainer}>
        <div className={styles.container}>
          <div className={styles.originalsHeader}>
            {influencerSlug ? (
              <h1
                className={styles.originalsTitle}
                style={{ cursor: 'pointer' }}
                onClick={handleHeaderClick}
              >
                <img
                  src={slaydTextIcon}
                  alt="Slayd"
                  className={styles.slaydTextLogo}
                />
                <span className={styles.xText}>x</span>
                <span className={styles.originalsTitleCursive}>
                  {influencerSlug}
                </span>
              </h1>
            ) : (
              <h1
                className={styles.originalsTitle}
                style={{ cursor: 'pointer' }}
                onClick={handleHeaderClick}
              >
                <img
                  src={slaydTextIcon}
                  alt="Slayd"
                  className={styles.slaydTextLogo}
                />
              </h1>
            )}
          </div>
          <div className={styles.heroSkeleton}>
            <div className={`${styles.skeleton} ${styles.skeletonCircle}`} />
            <div
              className={`${styles.skeleton} ${styles.skeletonText}`}
              style={{ width: '40%', height: '24px', marginTop: '16px' }}
            />
          </div>
          <div className={styles.sectionSkeleton}>
            <div
              className={`${styles.skeleton} ${styles.skeletonText}`}
              style={{ width: '30%', height: '16px' }}
            />
            <div
              className={`${styles.skeleton} ${styles.skeletonText}`}
              style={{ width: '90%', height: '14px', marginTop: '12px' }}
            />
            <div
              className={`${styles.skeleton} ${styles.skeletonText}`}
              style={{ width: '70%', height: '14px', marginTop: '8px' }}
            />
          </div>
          <div className={styles.divider} />
          <div className={styles.productSkeleton}>
            <div className={`${styles.skeleton} ${styles.skeletonImage}`} />
            <div className={styles.productSkeletonRight}>
              <div
                className={`${styles.skeleton} ${styles.skeletonText}`}
                style={{ width: '40%', height: '14px' }}
              />
              <div
                className={`${styles.skeleton} ${styles.skeletonText}`}
                style={{ width: '80%', height: '14px', marginTop: '8px' }}
              />
              <div
                className={`${styles.skeleton} ${styles.skeletonText}`}
                style={{ width: '60%', height: '14px', marginTop: '8px' }}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className={styles.parentContainer}>
        <div className={styles.container}>
          <div className={styles.originalsHeader}>
            {influencerSlug ? (
              <h1
                className={styles.originalsTitle}
                style={{ cursor: 'pointer' }}
                onClick={handleHeaderClick}
              >
                <img
                  src={slaydTextIcon}
                  alt="Slayd"
                  className={styles.slaydTextLogo}
                />
                <span className={styles.xText}>x</span>
                <span className={styles.originalsTitleCursive}>
                  {influencerSlug}
                </span>
              </h1>
            ) : (
              <h1
                className={styles.originalsTitle}
                style={{ cursor: 'pointer' }}
                onClick={handleHeaderClick}
              >
                <img
                  src={slaydTextIcon}
                  alt="Slayd"
                  className={styles.slaydTextLogo}
                />
              </h1>
            )}
          </div>
          <div className={styles.fallbackContainer}>
            <h3 className={styles.fallbackTitle}>{FALLBACK_TITLE}</h3>
            <p className={styles.fallbackSubtitle}>{FALLBACK_SUBTITLE}</p>
            <button
              className={styles.fallbackButton}
              onClick={handleFallbackClick}
            >
              {FALLBACK_BUTTON_TEXT}
            </button>
          </div>
        </div>
      </div>
    );
  }

  const productPrice = order.items.reduce((sum, item) => {
    const price = safeParseFloat(
      item.dealPriceAtPurchase || item.mrpAtPurchase
    );
    return sum + price * item.quantity;
  }, 0);
  const shippingFeeVal = safeParseFloat(order.shippingFee);
  const codFeeVal = safeParseFloat(order.codFee);
  const discountVal = safeParseFloat(order.promotionDiscount);
  const finalPriceVal =
    order.razorpayAmount &&
    order.razorpayAmount !== 'null' &&
    order.razorpayAmount !== 'undefined'
      ? safeParseFloat(order.razorpayAmount)
      : productPrice + shippingFeeVal + codFeeVal - discountVal;

  const products = order.items;

  return (
    <div className={styles.parentContainer}>
      <div className={styles.container}>
        <div className={styles.originalsHeader}>
          {influencerSlug ? (
            <h1
              className={styles.originalsTitle}
              style={{ cursor: 'pointer' }}
              onClick={handleHeaderClick}
            >
              <img
                src={slaydTextIcon}
                alt="Slayd"
                className={styles.slaydTextLogo}
              />
              <span className={styles.xText}>x</span>
              <span className={styles.originalsTitleCursive}>
                {influencerSlug}
              </span>
            </h1>
          ) : (
            <h1
              className={styles.originalsTitle}
              style={{ cursor: 'pointer' }}
              onClick={handleHeaderClick}
            >
              <img
                src={slaydTextIcon}
                alt="Slayd"
                className={styles.slaydTextLogo}
              />
            </h1>
          )}
        </div>

        <div className={styles.scrollContent}>
          <div className={styles.hero}>
            <img
              src={orderPlacedImage}
              alt={ORDER_PLACED_TITLE}
              className={styles.heroImage}
            />
            <h1 className={styles.heroTitle}>{ORDER_PLACED_TITLE}</h1>
          </div>

          <div className={styles.deliveryCard}>
            <h2 className={styles.deliveryTitle}>{SECTION_DELIVERY_ADDRESS}</h2>
            <p className={styles.addressText}>
              {order.shippingAddress
                ? [
                    order.shippingAddress.line1,
                    order.shippingAddress.line2,
                    order.shippingAddress.city,
                    order.shippingAddress.state &&
                      `${order.shippingAddress.state}${
                        order.shippingAddress.zipcode
                          ? ` - ${order.shippingAddress.zipcode}`
                          : ''
                      }`,
                  ]
                    .filter(Boolean)
                    .join(', ')
                : '—'}
            </p>
            <div className={styles.deliveryIconRow}>
              <img
                src={FeesIcon}
                alt="Estimate"
                className={styles.deliveryIcon}
              />
              <p className={styles.deliveryIconText}>
                {DELIVERY_ESTIMATE_TEXT}
              </p>
            </div>
            <div className={styles.deliveryIconRowLast}>
              <img
                src={ExchangeIcon}
                alt="Return Policy"
                className={styles.deliveryIcon}
              />
              <p className={styles.deliveryIconText}>{RETURN_POLICY_TEXT}</p>
            </div>
            <div className={styles.deliveryPartnerNotice}>
              <p className={styles.deliveryPartnerNoticeText}>
                {DELIVERY_PARTNER_TEXT}
              </p>
            </div>
          </div>

          <div className={styles.productCardContainer}>
            {products.map((item) => (
              <OrderItemWithTracking
                key={item.productId}
                item={item}
                guestOrderId={guestOrderId || ''}
              />
            ))}
          </div>

          <div className={styles.paymentCard}>
            <h2 className={styles.deliveryTitle}>{SECTION_PAYMENT_DETAILS}</h2>
            <div className={styles.paymentRow}>
              <span className={styles.paymentRowKey}>
                {LABEL_PRODUCT_PRICE}
              </span>
              <span className={styles.paymentRowValue}>
                {`₹${productPrice.toFixed(2)}`}
              </span>
            </div>
            {order.paymentMode?.toLowerCase() === 'cod' && (
              <div className={styles.paymentRow}>
                <span className={styles.paymentRowKey}>
                  {LABEL_DELIVERY_FEES}
                </span>
                <span className={styles.paymentRowValue}>
                  {`₹${(shippingFeeVal + codFeeVal).toFixed(2)}`}
                </span>
              </div>
            )}
            <div className={styles.paymentRow}>
              <span className={styles.paymentRowKey}>
                {LABEL_DELIVERY_FEES_STATIC}
              </span>
              <span className={styles.paymentRowValue}>
                <span className={styles.strikethrough}>
                  {`₹${DELIVERY_FEE.toFixed(2)}`}
                </span>
                {` ₹${(0).toFixed(2)}`}
              </span>
            </div>
            <div className={styles.paymentRow}>
              <span className={styles.paymentRowKey}>{LABEL_DISCOUNT}</span>
              <span className={styles.paymentRowValue}>
                {discountVal === 0 || discountVal == null
                  ? `₹${(0).toFixed(2)}`
                  : `-₹${discountVal.toFixed(2)}`}
              </span>
            </div>
            <div className={styles.paymentRow}>
              <span className={styles.paymentRowKeyBold}>
                {LABEL_FINAL_PRICE}
              </span>
              <span className={styles.paymentRowValueFinal}>
                {`₹${finalPriceVal.toFixed(2)}`}
              </span>
            </div>
          </div>

          <div className={styles.paymentModeCard}>
            <h2 className={styles.deliveryTitle}>{SECTION_MODE_OF_PAYMENT}</h2>
            <p className={styles.paymentMethodText}>
              {getPaymentModeLabel(order.paymentMode)}
            </p>
          </div>

          {order.status !== OrderStatus.CANCELLED &&
            order.status !== 'FAILED' && (
              <div className={styles.actionCard}>
                <button
                  className={styles.actionRow}
                  onClick={() => handleActionClick('track_order')}
                >
                  <span className={styles.actionRowText}>
                    {LABEL_TRACK_ORDER}
                  </span>
                  <img
                    src={ForwardArrow}
                    alt="Go"
                    className={styles.actionArrow}
                  />
                </button>
                {order.status !== OrderStatus.RETURN_REQUESTED &&
                  order.status !== OrderStatus.REFUNDED && (
                    <button
                      className={styles.actionRow}
                      onClick={() => handleActionClick('cancel_return')}
                    >
                      <span className={styles.actionRowText}>
                        {LABEL_CANCEL_RETURN}
                      </span>
                      <img
                        src={ForwardArrow}
                        alt="Go"
                        className={styles.actionArrow}
                      />
                    </button>
                  )}
              </div>
            )}
        </div>
      </div>
    </div>
  );
}
